import { motion, AnimatePresence } from "motion/react"
import {
  Building07,
  Car01,
  Check,
  CurrencyDollar,
  LayersThree01,
  MarkerPin01,
  Tool01,
  UserCircle,
  Users03,
} from "@untitledui/icons"
import type { ReactNode } from "react"
import type { TeamType } from "@/lib/api/types"
import { useAuth } from "@/lib/auth/auth-context"
import { useOnboarding, type OnboardingStep } from "@/lib/onboarding/onboarding-context"
import { cx } from "@/lib/utils/cx"

// One persistent panel rendered in the right column of every onboarding step.
// It builds as the user progresses — typing your name fills the avatar, picking
// an org type swaps the icon tile, completing a step lights up its checklist
// row. The intent: replace four bespoke "preview cards" with a single evolving
// context surface so the wizard feels continuous instead of episodic.
//
// Step 4 also renders a teaching card *below* this panel via the `teaching`
// prop — those per-type cards (estimate flow / sample line item / location
// network) are the only previews that genuinely teach, so they stay; the
// data-mirror previews go away.

const SPRING = { type: "spring", stiffness: 300, damping: 28 } as const

const TYPE_META: Record<
  TeamType,
  {
    label: string
    icon: typeof Building07
    gradient: string
    shadow: string
    glow: string
    // Role the team's creator will land in once onboarding finishes. Mirrors
    // teamTypeFallbackRole in lib/api/users.ts — surfacing it during step 2
    // so the user sees "I'll be set up as Body Shop Manager" the moment they
    // pick their org type, instead of finding out post-hoc.
    creatorRoleLabel: string
  }
> = {
  Dealership: {
    label: "Dealership",
    icon: Car01,
    gradient: "from-sky-500 to-sky-700",
    shadow: "shadow-sky-600/25",
    glow: "from-sky-200/60 to-transparent",
    creatorRoleLabel: "General Manager",
  },
  Bodyshop: {
    label: "Body Shop",
    icon: Tool01,
    gradient: "from-amber-500 to-amber-700",
    shadow: "shadow-amber-600/25",
    glow: "from-amber-200/60 to-transparent",
    creatorRoleLabel: "Body Shop Manager",
  },
  DealershipGroup: {
    label: "Group",
    icon: LayersThree01,
    gradient: "from-indigo-500 to-indigo-700",
    shadow: "shadow-indigo-600/25",
    glow: "from-indigo-200/60 to-transparent",
    creatorRoleLabel: "Director",
  },
}


function initialsOf(firstName: string, lastName: string): string {
  const f = firstName.trim()[0] ?? ""
  const l = lastName.trim()[0] ?? ""
  const r = `${f}${l}`.toUpperCase()
  return r || "—"
}

interface PanelChecklistRow {
  key: OnboardingStep
  label: string
  detail: string | null
  done: boolean
  active: boolean
  icon: typeof UserCircle
}

export function OnboardingContextPanel({ teaching }: { teaching?: ReactNode }) {
  const { user } = useAuth()
  const {
    step,
    selectedType,
    selectedRole,
    createdTeamId,
    companySnapshot,
    invitedCount,
    profileDraft,
  } = useOnboarding()

  // The draft mirror updates on every keystroke in step 1; auth.user only
  // updates after submit. Prefer the draft so the panel feels live.
  const liveFirst = profileDraft.firstName || user?.firstName || ""
  const liveLast = profileDraft.lastName || user?.lastName || ""
  const fullName = [liveFirst, liveLast].filter(Boolean).join(" ").trim()
  const meta = selectedType ? TYPE_META[selectedType] : null
  const TypeIcon = meta?.icon ?? Building07

  const orgName = companySnapshot?.name?.trim() || ""
  const cityState = companySnapshot
    ? [companySnapshot.city, companySnapshot.state].filter(Boolean).join(", ")
    : ""

  const rows: PanelChecklistRow[] = [
    {
      key: "personal",
      label: "Your profile",
      detail: fullName || null,
      done: !!fullName,
      active: step === "personal",
      icon: UserCircle,
    },
    {
      key: "company",
      label: "Workspace",
      detail: orgName || null,
      // Considered "done" once the user has both picked an org type and
      // typed a name. The team isn't actually POSTed until step 2b
      // submits, but checking off Identity early makes the right-column
      // checklist feel honest about what the user has *committed* to.
      done: !!orgName && !!selectedType,
      active: step === "company",
      icon: Building07,
    },
    {
      key: "location",
      label: "Where",
      detail: cityState || null,
      done: !!createdTeamId,
      active: step === "location",
      icon: MarkerPin01,
    },
    {
      key: "invite",
      label: "Team",
      detail:
        invitedCount > 0
          ? `${invitedCount} ${invitedCount === 1 ? "invite" : "invites"} sent`
          : null,
      done: invitedCount > 0,
      active: step === "invite",
      icon: Users03,
    },
    {
      key: "extra",
      label:
        selectedType === "Dealership"
          ? "Body shop"
          : selectedType === "Bodyshop"
            ? "Shop rates"
            : selectedType === "DealershipGroup"
              ? "Dealerships"
              : "Setup",
      detail: null,
      done: false,
      active: step === "extra",
      icon:
        selectedType === "Bodyshop"
          ? CurrencyDollar
          : selectedType === "DealershipGroup"
            ? Building07
            : Building07,
    },
    // Group-only — body shops are a step of their own after dealerships.
    {
      key: "bodyshops",
      label: "Body shops",
      detail: null,
      done: false,
      active: step === "bodyshops",
      icon: Tool01,
    },
  ]
  // Filter to whichever subset the active flow visits. Groups skip "invite"
  // (per-team invites happen on the dealerships/shops they declare). Non-groups
  // skip "bodyshops" (their step 5 is the existing `extra`).
  const visibleRows = rows.filter((r) =>
    selectedType === "DealershipGroup" ? r.key !== "invite" : r.key !== "bodyshops",
  )

  return (
    <div className="w-full max-w-sm space-y-4">
      {/* ── Workspace card ─────────────────────────────── */}
      <div className="relative rounded-3xl bg-white border border-zinc-200/70 shadow-xl shadow-zinc-900/[0.06] overflow-hidden">
        {/* Soft ambient glow at the top — colour-shifts with the chosen org type
            and gives the card depth without being busy. */}
        <div
          aria-hidden
          className={cx(
            "absolute inset-x-0 top-0 h-24 bg-gradient-to-b",
            meta?.glow ?? "from-emerald-200/50 to-transparent",
          )}
        />
        <div className="relative p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            Your workspace
          </p>

          <div className="mt-4 flex items-center gap-3.5">
            {/* Tile — swaps to org-type colour once chosen, falls back to avatar */}
            <AnimatePresence mode="wait">
              {meta ? (
                <motion.div
                  key={`tile-${meta.label}`}
                  initial={{ rotate: -8, scale: 0.85, opacity: 0 }}
                  animate={{ rotate: 0, scale: 1, opacity: 1 }}
                  exit={{ rotate: 8, scale: 0.85, opacity: 0 }}
                  transition={SPRING}
                  className={cx(
                    "size-14 rounded-2xl grid place-items-center shrink-0 shadow-lg",
                    `bg-gradient-to-br ${meta.gradient} ${meta.shadow}`,
                  )}
                >
                  <TypeIcon className="size-7 text-white" strokeWidth={2} aria-hidden />
                </motion.div>
              ) : (
                <motion.div
                  key="tile-empty"
                  initial={{ scale: 0.92, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.92, opacity: 0 }}
                  transition={SPRING}
                  className="size-14 rounded-2xl grid place-items-center shrink-0 bg-gradient-to-br from-emerald-500 to-emerald-700 text-white text-[15px] font-bold shadow-lg shadow-emerald-600/25"
                >
                  {initialsOf(liveFirst, liveLast)}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="min-w-0 flex-1">
              <AnimatePresence mode="wait">
                <motion.p
                  key={orgName || "no-name"}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18 }}
                  className={cx(
                    "text-[15px] font-semibold truncate",
                    orgName ? "text-zinc-900" : "text-zinc-400",
                  )}
                >
                  {orgName || "Your business name"}
                </motion.p>
              </AnimatePresence>
              <p className="text-[12px] text-zinc-500 mt-0.5 truncate">
                {meta ? meta.label : "Pick a type next"}
              </p>
            </div>
          </div>

          {/* Role preview — appears once an org type is picked. Reflects the
              user's actual role pick (selectedRole) when set; falls back to
              the type-default while they're still on the type tiles. */}
          {meta && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-5 flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50/60 border border-emerald-200/60"
            >
              <UserCircle className="size-3.5 text-emerald-600 shrink-0" aria-hidden />
              <p className="text-[11.5px] text-emerald-900/85 truncate">
                You'll be set up as{" "}
                <span className="font-semibold text-emerald-900">
                  {selectedRole || meta.creatorRoleLabel}
                </span>
              </p>
            </motion.div>
          )}

          {/* Checklist */}
          <ul className="mt-5 space-y-2">
            {visibleRows.map((row) => (
              <ChecklistRow key={row.key} row={row} />
            ))}
          </ul>
        </div>
      </div>

      {/* ── Step 4 teaching card slot ─────────────────── */}
      {teaching && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        >
          {teaching}
        </motion.div>
      )}
    </div>
  )
}

function ChecklistRow({ row }: { row: PanelChecklistRow }) {
  const Icon = row.icon
  return (
    <li
      className={cx(
        "flex items-center gap-3 py-1.5",
        row.active && "bg-emerald-50/50 -mx-2 px-2 rounded-lg",
      )}
    >
      <motion.div
        initial={false}
        animate={{
          backgroundColor: row.done
            ? "rgb(16 185 129)"
            : row.active
              ? "rgb(255 255 255)"
              : "rgb(255 255 255)",
          borderColor: row.done
            ? "rgb(16 185 129)"
            : row.active
              ? "rgb(16 185 129)"
              : "rgb(228 228 231)",
          color: row.done ? "rgb(255 255 255)" : "rgb(161 161 170)",
        }}
        transition={{ duration: 0.3 }}
        className="size-6 rounded-full border-2 grid place-items-center shrink-0"
      >
        {row.done ? (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
          >
            <Check className="size-3" strokeWidth={3} aria-hidden />
          </motion.span>
        ) : (
          <Icon className="size-3" aria-hidden />
        )}
      </motion.div>
      <div className="min-w-0 flex-1">
        <p
          className={cx(
            "text-[12.5px] font-semibold truncate transition-colors duration-200",
            row.done
              ? "text-zinc-900"
              : row.active
                ? "text-emerald-800"
                : "text-zinc-500",
          )}
        >
          {row.label}
        </p>
        {row.detail && (
          <p className="text-[11px] text-zinc-400 truncate">{row.detail}</p>
        )}
        {!row.detail && row.active && (
          <p className="text-[11px] text-emerald-600/80 truncate font-medium">
            In progress
          </p>
        )}
      </div>
    </li>
  )
}

/* ── Step 4 teaching cards ───────────────────────────────
 *
 * The only previews that pull weight: estimate flow / sample line item /
 * location network. Used as the `teaching` slot of the panel on step 4.
 */

export function DealershipFlowTeaching({
  dealershipName,
  bodyshopName,
}: {
  dealershipName: string
  bodyshopName: string
}) {
  return (
    <TeachingCard label="What you're connecting">
      <MiniCard
        icon={Car01}
        gradient="from-sky-500 to-sky-700"
        shadow="shadow-sky-600/25"
        title={dealershipName || "Your dealership"}
        sub="Sends estimate"
      />
      <Connector />
      <MiniCard
        icon={Tool01}
        gradient="from-amber-500 to-amber-700"
        shadow="shadow-amber-600/25"
        title={bodyshopName.trim() || "Body shop"}
        sub="Receives & quotes"
        placeholder={!bodyshopName.trim()}
      />
    </TeachingCard>
  )
}

export function BodyShopRateTeaching({ bodyLabor }: { bodyLabor: string }) {
  const rate = Number.parseFloat(bodyLabor)
  const valid = Number.isFinite(rate) && rate > 0
  const sample = valid ? rate * 4.5 : 0
  return (
    <TeachingCard label="Sample line item">
      <div className="rounded-2xl bg-white border border-zinc-200/70 shadow-md shadow-zinc-900/5 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="size-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 grid place-items-center shadow-sm shadow-amber-600/25">
            <CurrencyDollar className="size-5 text-white" aria-hidden />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-zinc-900">4.5 hrs body labor</p>
            <p className="text-[11px] text-zinc-400">Typical mid-size repair</p>
          </div>
        </div>
        <div className="border-t border-zinc-100 pt-3 space-y-2">
          <KV k="Hours" v="4.5" />
          <KV k="Rate" v={valid ? `$${rate.toFixed(2)} / hr` : "—"} dim={!valid} />
          <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
            <span className="text-[12.5px] font-semibold text-zinc-900">Subtotal</span>
            <span
              className={cx(
                "text-[15px] font-bold tabular-nums",
                valid ? "text-zinc-900" : "text-zinc-300",
              )}
            >
              {valid ? `$${sample.toFixed(2)}` : "$—"}
            </span>
          </div>
        </div>
      </div>
    </TeachingCard>
  )
}

export function GroupNetworkTeaching({
  dealerships,
  bodyshops,
  connectionCount,
}: {
  dealerships: Array<{ name: string; city?: string; state?: string }>
  bodyshops: Array<{ name: string; city?: string; state?: string }>
  connectionCount: number
}) {
  const filledDealerships = dealerships.filter((d) => d.name.trim().length > 0)
  const filledBodyshops = bodyshops.filter((b) => b.name.trim().length > 0)

  // Top-down org sketch: Group → Dealerships stack → Body shops stack.
  // A small connection counter sits at the bottom so the user can see the
  // wiring picking up as they fill in the matrix. No SVG lines: too easy to
  // get wrong, too easy to mislead about which dealership routes to which
  // shop. The connection counter + the form's chip toggles are clearer.
  return (
    <TeachingCard label="Your group">
      <MiniCard
        icon={LayersThree01}
        gradient="from-indigo-500 to-indigo-700"
        shadow="shadow-indigo-600/25"
        title="Group"
        sub={`${filledDealerships.length} ${
          filledDealerships.length === 1 ? "dealership" : "dealerships"
        }${
          filledBodyshops.length > 0
            ? ` · ${filledBodyshops.length} body shop${
                filledBodyshops.length === 1 ? "" : "s"
              }`
            : ""
        }`}
      />

      <Connector />

      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
          Dealerships
        </p>
        {filledDealerships.length === 0 ? (
          <MiniCard
            icon={Building07}
            gradient="from-zinc-400 to-zinc-600"
            shadow="shadow-zinc-500/15"
            title="Dealership 1"
            sub="Add your primary location"
            placeholder
          />
        ) : (
          filledDealerships.slice(0, 3).map((d, i) => {
            const cityState = [d.city, d.state].filter(Boolean).join(", ") || "Dealership"
            return (
              <motion.div
                key={`d-${i}`}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
              >
                <MiniCard
                  icon={Building07}
                  gradient="from-sky-500 to-sky-700"
                  shadow="shadow-sky-600/25"
                  title={d.name}
                  sub={i === 0 ? `${cityState} · primary` : cityState}
                />
              </motion.div>
            )
          })
        )}
        {filledDealerships.length > 3 && (
          <p className="text-[11px] text-zinc-400 text-center pt-1">
            +{filledDealerships.length - 3} more
          </p>
        )}
      </div>

      {filledBodyshops.length > 0 && (
        <>
          <Connector />
          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
              Body shops
            </p>
            {filledBodyshops.slice(0, 3).map((b, i) => {
              const cityState =
                [b.city, b.state].filter(Boolean).join(", ") || "Body shop"
              return (
                <motion.div
                  key={`b-${i}`}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <MiniCard
                    icon={Tool01}
                    gradient="from-amber-500 to-amber-700"
                    shadow="shadow-amber-600/25"
                    title={b.name}
                    sub={cityState}
                  />
                </motion.div>
              )
            })}
            {filledBodyshops.length > 3 && (
              <p className="text-[11px] text-zinc-400 text-center pt-1">
                +{filledBodyshops.length - 3} more
              </p>
            )}
          </div>
        </>
      )}

      {connectionCount > 0 && (
        <p className="text-[11px] text-zinc-500 text-center pt-1">
          {connectionCount} connection{connectionCount === 1 ? "" : "s"} wired
        </p>
      )}
    </TeachingCard>
  )
}

/* ── Shared bits ─────────────────────────────────────────── */

function TeachingCard({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-3xl bg-white border border-zinc-200/70 shadow-xl shadow-zinc-900/[0.06] p-5">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-4">
        {label}
      </p>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

function MiniCard({
  icon: Icon,
  gradient,
  shadow,
  title,
  sub,
  placeholder,
}: {
  icon: typeof Building07
  gradient: string
  shadow: string
  title: string
  sub: string
  placeholder?: boolean
}) {
  return (
    <div
      className={cx(
        "flex items-center gap-3 p-3 rounded-xl bg-white border shadow-sm shadow-zinc-900/[0.04]",
        placeholder ? "border-dashed border-zinc-200" : "border-zinc-200/70",
      )}
    >
      <div
        className={cx(
          "size-9 rounded-xl bg-gradient-to-br grid place-items-center shrink-0 shadow-md",
          gradient,
          shadow,
        )}
      >
        <Icon className="size-4 text-white" aria-hidden />
      </div>
      <div className="min-w-0">
        <p
          className={cx(
            "text-[13px] font-semibold truncate",
            placeholder ? "text-zinc-400" : "text-zinc-900",
          )}
        >
          {title}
        </p>
        <p className="text-[11px] text-zinc-400 truncate">{sub}</p>
      </div>
    </div>
  )
}

function Connector() {
  return (
    <div className="flex items-center justify-center">
      <div className="h-5 w-0.5 bg-gradient-to-b from-zinc-300 to-zinc-200 rounded-full" />
    </div>
  )
}

function KV({ k, v, dim }: { k: string; v: string; dim?: boolean }) {
  return (
    <div className="flex items-center justify-between text-[12px]">
      <span className="text-zinc-500">{k}</span>
      <span
        className={cx(
          "font-medium tabular-nums",
          dim ? "text-zinc-300" : "text-zinc-700",
        )}
      >
        {v}
      </span>
    </div>
  )
}
